// src/app/layout/sidebar/sidebar.service.ts
import { Injectable, signal, computed } from '@angular/core';
import { SIDEBAR_LAYOUT } from './sidebar.constants';

/**
 * Service to manage sidebar state globally.
 * Allows other components to react to sidebar collapse/expand.
 */
@Injectable({
  providedIn: 'root'
})
export class SidebarService {
  private readonly mobileBreakpoint = SIDEBAR_LAYOUT.mobileBreakpointPx;

  /** Whether the sidebar is currently collapsed (icon-only mode) */
  private _isCollapsed = signal<boolean>(this.loadState());
  private _viewportWidth = signal<number>(this.readViewportWidth());
  private _isMobileVisible = signal<boolean>(false);

  /** Observable collapsed state */
  readonly isCollapsed = this._isCollapsed.asReadonly();
  readonly isMobileVisible = this._isMobileVisible.asReadonly();
  readonly isMobileViewport = computed(() =>
    this._viewportWidth() <= this.mobileBreakpoint
  );
  readonly isMobileOverlayOpen = computed(() =>
    this.isMobileViewport() && this._isMobileVisible()
  );

  /** Width of sidebar based on state */
  readonly expandedWidth = SIDEBAR_LAYOUT.expandedWidth;
  readonly collapsedWidth = SIDEBAR_LAYOUT.collapsedWidth;
  readonly sidebarRenderWidth = computed(() =>
    this.isMobileViewport()
      ? Math.min(this.expandedWidth, this._viewportWidth())
      : this._isCollapsed() ? this.collapsedWidth : this.expandedWidth
  );
  readonly sidebarWidth = computed(() =>
    this.isMobileViewport()
      ? 0
      : this._isCollapsed() ? this.collapsedWidth : this.expandedWidth
  );

  constructor() {
    this.bindViewportListener();
  }

  /**
   * Toggle sidebar interaction mode:
   * - desktop: collapse/expand
   * - mobile: open/close overlay visibility
   */
  toggle(): void {
    if (this.isMobileViewport()) {
      this.toggleMobileVisibility();
      return;
    }

    this.setCollapsed(!this._isCollapsed());
  }

  /**
   * Expand sidebar (show full labels)
   */
  expand(): void {
    this.setCollapsed(false);
  }

  /**
   * Collapse sidebar (icon-only mode)
   */
  collapse(): void {
    this.setCollapsed(true);
  }

  openMobile(): void {
    if (!this.isMobileViewport()) {
      return;
    }
    this._isMobileVisible.set(true);
  }

  closeMobile(): void {
    this._isMobileVisible.set(false);
  }

  toggleMobileVisibility(): void {
    if (!this.isMobileViewport()) {
      return;
    }
    this._isMobileVisible.update((isVisible) => !isVisible);
  }

  syncViewport(width = this.readViewportWidth()): void {
    this._viewportWidth.set(width);

    if (!this.isMobileViewport()) {
      this.closeMobile();
    }
  }

  /**
   * Set sidebar state explicitly
   */
  setCollapsed(collapsed: boolean): void {
    this._isCollapsed.set(collapsed);
    this.saveState(collapsed);
  }

  /**
   * Load saved state from localStorage
   * Default is expanded (false) - sidebar should be expanded by default on dashboard
   */
  private loadState(): boolean {
    if (typeof localStorage === 'undefined') return false;
    const saved = localStorage.getItem(SIDEBAR_LAYOUT.storageKey);
    // Default to expanded (false) if no preference saved
    if (saved === null) return false;
    return saved === 'true';
  }

  /**
   * Save state to localStorage
   */
  private saveState(collapsed: boolean): void {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(SIDEBAR_LAYOUT.storageKey, collapsed.toString());
  }

  private readViewportWidth(): number {
    if (typeof window === 'undefined') {
      return Number.POSITIVE_INFINITY;
    }

    return window.innerWidth;
  }

  private bindViewportListener(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener('resize', this.handleViewportResize, { passive: true });
  }

  private handleViewportResize = (): void => {
    this.syncViewport();
  };
}
